import { Avatar, AvatarImage, Label } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

type Profile = {
  name: string;
  title: string;
  bio: string;
  type: 'leadership' | 'board' | 'advisor';
  image: string;
  customStyle?: string;
};

const profiles: Profile[] = [
  {
    name: 'Alexander St. John',
    title: 'CEO',
    bio: "Alex St. John is a technology entrepreneur and inventor known for Co-creating the DirectX Media Architecture and Direct3D API at Microsoft.  St. John founded WildTangent Inc. which became the world's largest online game publisher and later Nyriad Ltd. to pioneer GPU accelerated scientific computing. He is the subject of the book:  'Renegades of the Empire'.",
    type: 'leadership',
    image: '/images/profiles/Alex2.jpg',
  },
  {
    name: 'Trey Overton',
    title: 'CTO',
    bio: 'Software engineer and architect across myriad industries, building web properties from the very beginning in the 1990s; Did a great stint at Microsoft; Game designer of tabletop miniature wargames, Shock Force and GWAR: Rumble in Antarctica; Officer in the Tennessee State Guard.',
    type: 'leadership',
    image: '/images/profiles/Trey.png',
  },
  {
    name: 'William “Bing” Gordon',
    title: 'Board Member',
    bio: "William 'Bing' Gordon is a video game executive and technology venture capitalist. He served ten years as Chief Creative Officer of video game publisher and developer Electronic Arts (EA) prior to his current partnership with Kleiner Perkins Caufield & Byers (KPCB). He was a founding director of Audible.com and has served on several high-profile Boards of Directors including Amazon, Ngmoco, Duolingo, and Zynga.",
    type: 'board',
    image: '/images/profiles/Bing.jpg',
  },
  {
    name: 'John Salter',
    title: 'Board Member',
    bio: 'John is a Co-Founder and Partner of The Raine Group, a global merchant bank focused on Technology, Media and Telecommunications. John has dedicated his career to investing and advising in global leaders in the gaming industry. John has served on the boards of DraftKings, Games 24x7, Huuuge, Midnite, and Jagex, among others.',
    type: 'board',
    image: '/images/profiles/John.jpg',
  },
  {
    name: 'Bill Gossman',
    title: 'Investor/Advisor',
    bio: 'Bill Gossman is a seasoned entrepreneur, executive, and start-up investor with expertise spanning digital media, healthcare, SAAS platforms, photonics, and AI. He is currently lead director at nLight (LASR) and serves on the board of HealthTap. Bill regularly advises entrepreneurs and has been CEO of several tech companies. Previously, he was a venture partner at Mohr Davidow Ventures and founded @mobile, which he sold for $400M. He also held key roles at Bell Labs, AT&T, and Northrop. Bill holds 14 patents and earned engineering degrees from Cornell and MIT, as well as an MBA from the University of Maryland.',
    type: 'advisor',
    image: '/images/profiles/Bill.jpg',
  },
  {
    name: 'Dave Madden',
    title: 'Investor/Advisor',
    bio: "Media Technology Entrepreneur. Founder, Investor, Advisor, Board Member & Team Builder. Expertise in creating and executing video game business models and services. Games Platform development, live service monetization, advertising and sponsorships. A creator of the 'rewarded' or 'watch to earn' dynamic brand sponsorship model and now the de facto ad monetization and free access model for streaming services like Spotify and F2P/live service games. Founding Chairman of the IAB Games Committee.",
    type: 'advisor',
    image: '/images/profiles/Dave.jpg',
  },
  {
    name: 'Doug Scott',
    title: 'Investor/Advisor',
    bio: 'Doug Scott has extensive experience in marketing and executive leadership roles, primarily in the tech and entertainment (streaming, gaming, sports, music) industries. He has held Chief Marketing Officer positions at Twitch, Zynga, and BandPage, and, prior to that, held a variety of marketing roles at DeNA, RMG Networks, and Electronic Arts.  Doug was the founding CEO of social gaming company Lionside and most recently oversaw Twitch’s international operations.  He also serves as an active advisor and board member for multiple companies.',
    type: 'advisor',
    image: '/images/profiles/Doug.jpg',
  },
  {
    name: 'Benjamin Höglinger-Stelzer',
    title: 'Technology Advisor',
    bio: 'Benjamin “Nefarius” Höglinger-Stelzer has been a pioneering contributor and creator of many leading gaming technologies for the PC, including creating a battle-tested gamepad emulation framework. His expertise at enabling new gaming solutions through low-level drivers was instrumental to Playcasts development.',
    type: 'advisor',
    image: '/images/profiles/Benjamin.jpg',
    customStyle: 'text-md',
  },
];

export const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div className='text-white bg-gray-800 flex flex-1 justify-center'>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 px-4 py-8 max-w-4xl flex-1'>
        <div className='flex flex-col flex-wrap gap-10'>
          <div className='flex justify-center'>
            <Label className='font-[Syne] text-xl font-bold'>Leadership</Label>
          </div>
          {profiles
            .filter((profile) => profile.type === 'leadership')
            .map((profile) => (
              <div key={profile.name}>
                <ProfileCard {...profile} />
              </div>
            ))}
        </div>
        <div className='flex flex-col flex-wrap gap-10'>
          <div className='flex justify-center'>
            <Label className='font-[Syne] text-xl font-bold'>The Board</Label>
          </div>
          {profiles
            .filter((profile) => profile.type === 'board')
            .map((profile) => (
              <div key={profile.name}>
                <ProfileCard {...profile} />
              </div>
            ))}
        </div>
        <div className='flex flex-col flex-wrap gap-10'>
          <div className='flex justify-center'>
            <Label className='font-[Syne] text-xl font-bold'>Advisors</Label>
          </div>
          {profiles
            .filter((profile) => profile.type === 'advisor')
            .map((profile) => (
              <div key={profile.name}>
                <ProfileCard {...profile} />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

const ProfileCard = ({ name, bio, title, image, customStyle }: Profile) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleProfileToggle = () => {
    if (isOpen) {
      if (divRef.current) {
        divRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }

      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <div className='relative flex flex-col flex-1 justify-between items-center gap-2 h-44'>
      <Avatar className='size-24'>
        <AvatarImage src={image} alt={name} className='size-24' />
      </Avatar>
      <div
        ref={divRef}
        className={clsx(
          isOpen ? 'max-h-44 h-44 overflow-auto' : 'max-h-16 h-16 overflow-hidden',
          'absolute bottom-0 flex flex-col bg-gray-900 rounded-md w-full px-4 py-2 shadow-2xl cursor-pointer transition-all ease-in-out select-none opacity-90',
        )}
        onClick={handleProfileToggle}
      >
        <span className='text-gray-400 text-sm'>{title}</span>
        <Label className={clsx('font-[Syne] text-xl font-bold text-yellow-400 cursor-pointer', customStyle)}>{name}</Label>
        <p className='pt-2'>{bio}</p>
      </div>
    </div>
  );
};

